import { useMemo } from 'react';
import { usePortfolioSpeciesSummaryQuery } from 'src/app/portfolio/state/api/portfolioSpeciesGraphSlice';
import type { PortfolioSpeciesSummary } from 'src/app/portfolio/state/api/portfolioSpeciesQuery';
import type { SpeciesSummaryCardsData } from '../state/api/portfolioQuery';

/**
 * Hardcoded in the Go resolver for both cards rather than derived from any species,
 * so the grid path states the same constant instead of dropping the chips.
 */
const REPORTING_FRAMEWORKS = ['TNFD', 'CSRD', 'GRI'];

type Args = {
    customerId: string;
    filters?: { name: string; values: string[] }[];
};

/**
 * The portfolio summary's species cards. The grid is the only source since
 * DD-5858; it names almost every field differently and has no per-portfolio
 * run date, so it is mapped into the card shape the components always read.
 */
export const useSpeciesSummaryCards = ({ customerId, filters }: Args) => {
    const args = { customerId, ...(filters?.length ? { filters } : {}) };
    const grid = usePortfolioSpeciesSummaryQuery(args, { skip: !customerId });

    // Memoised because the summary page assembles a useMemo from this. A fresh object
    // each render restarts that chain indefinitely.
    const data = useMemo(() => (grid.currentData ? toCardData(grid.currentData) : null), [grid.currentData]);

    return { data, isFetching: grid.isFetching };
};

const toCardData = (summary: PortfolioSpeciesSummary): SpeciesSummaryCardsData => ({
    numberSites: summary.siteCount,
    totalCount: summary.totalSpeciesCount,
    redListSpecies: {
        siteCount: summary.redListSiteCount,
        count: summary.redListSpeciesCount,
        frameworks: REPORTING_FRAMEWORKS,
        threatenedCount: summary.threatenedSpeciesCount,
        // The chart reads iucnRedListCategory; the grid calls it category.
        chartData: summary.iucn.map(entry => ({ iucnRedListCategory: entry.category, count: entry.count })),
    },
    invasiveSpecies: {
        count: summary.invasiveSpeciesCount,
        siteCount: summary.invasiveSiteCount,
        frameworks: REPORTING_FRAMEWORKS,
    },
});
